import Daily from "@daily-co/daily-js";
import { DailyProvider } from "@daily-co/daily-react";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import { LessonState } from "../common/constants";
import useBeforeUnload from "../common/hooks/useBeforeUnload";
import usePrompt from "../common/hooks/usePrompt";
import { PiPProvider } from "../common/PiPContext";
import Button from "../components/Button";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import Loader from "../components/Loader";
import Room from "../components/Room";
import typo from "../typography.module.css";
import { formatDateV2, formatTimeV2, UTCtoLocal } from "../utils";
import styles from "./LiveLesson.module.css";
// import RoomTest from "../components/RoomTest";
// import { TourProvider } from '@reactour/tour'
import MainContext from "../common/MainContext";
import Lesson from "../components/Lesson";

const LiveLesson = () => {

    const context = useContext(MainContext)
    const { roomName } = useParams();
    const navigate = useNavigate()
    const [lesson, setLesson] = useState(null)
    const [call, setCall] = useState(null)
    const [token, setToken] = useState(null)
    const [startInterval, setStartInterval] = useState(null)
    const [remainingTime, setRemainingTime] = useState(null)
    const [lessonState, setLessonState] = useState(null)
    const [roomError, setRoomError] = useState(null)
    const [meetingState, setMeetingState] = useState(null)
    const [isDarkLessonTheme, setIsDarkLessonTheme] = useState(false)

    const [callPreAuthorized, setCallPreAuthorized] = useState(false)
    
    const [testCompleted, setTestCompleted] = useState(true) //bypassing test for now

    usePrompt('Sei sicuro di voler abbandonare la lezione ?', !!call && !['error', 'left-meeting'].includes(meetingState) && testCompleted)
    useBeforeUnload('Sei sicuro di voler abbandonare la lezione ?', !!call && !['error', 'left-meeting'].includes(meetingState) && testCompleted)
    useEffect(() => {
        document.body.style['overscroll-behavior-x'] = 'none';
        setIsDarkLessonTheme(localStorage.getItem('lesson-theme') === 'dark')
    }, [])

    useEffect(() => {
        const getLesson = async (roomName) => {
            try {
                const lesson = await api.get(`/teacher/lessons/${roomName}`);
                setLesson(lesson);
            } catch (e) {
                console.error(e);
            }
        }

        if (roomName) {
            getLesson(roomName);
        }
    }, [roomName])

    useEffect(() => {
        if (!lesson) return;
        if (lesson.type === 'recorded') {
            setLessonState(LessonState.Ended)
            return
        }

        const interval = setInterval(() => {
            getRemainingTime()
        }, 1000)

        setStartInterval(interval)

        const getLessonMaterial = async (lessonId) => {
            try {
                const materials = await api.get(`/teacher/lessons/${lessonId}/materials`);

                setLesson((prev) => {
                    prev.materials = materials;
                    return { ...prev };
                });
            } catch (e) {
                console.error(e);
            }
        };

        const getModuleMaterial = async (moduleId) => {
            try {
                const materials = await api.get(`/teacher/modules/${moduleId}/materials`);

                setLesson((prev) => {
                    prev.module_materials = materials.filter(material => !prev.materials?.some(existing => existing.id === material.id));
                    return { ...prev };
                });
            } catch (e) {
                console.error(e);
            }
        }

        if (!lesson.materials) {
            getLessonMaterial(lesson.id);
            getModuleMaterial(lesson.module.id);
        }

        return () => {
            clearInterval(interval);
        };
    }, [lesson])

    useEffect(() => {
        if (lesson && remainingTime) {
            if (lesson.ended_at || lesson.terminated_at) {
                setLessonState(LessonState.Ended)
                clearInterval(startInterval)
                setStartInterval(null)
            }
            else if (remainingTime < 7200) {
                if (testCompleted === null) {
                    getLessonParticipant(lesson.id)
                }
                setLessonState(LessonState.Live)
                clearInterval(startInterval)
                setStartInterval(null)
            }
            else {
                setLessonState(LessonState.TooEarly)
            }
        }

    }, [remainingTime, lesson])

    useEffect(() => {
        if (lessonState === LessonState.Live) {
            getMeetingToken()
        }
    }, [lessonState])

    useEffect(() => {
        if (token !== null) {
            context.setSidenav({ open: false, visible: false })
            if (testCompleted) {
                startCall()
            }
            else {
                preAuthCall()
            }
        }
    }, [token])

    useEffect(() => {
        if (call) {
            call.on('error', (e) => {
                setRoomError(e)
                setMeetingState('error')
            })
            call.on('left-meeting', (e) => {
                setRoomError(e)
                setMeetingState('left-meeting')
            })
        }

        return () => {
            if (call) {
                call.leave();
                call.destroy();
            }
        };
    }, [call])

    const getRemainingTime = () => {
        const now = new Date().getTime();
        const start = UTCtoLocal(lesson.starts_at).getTime();
        setRemainingTime(parseInt((start - now) / 1000))
        return parseInt((start - now) / 1000);
    }

    const startCall = async () => {
        try {
            if (!call) {
                const call = Daily.createCallObject({ url: `https://startingfinance.daily.co/${lesson.room_name}`, token: token ?? '' })
                setCall(call)
                await call.join()
            }
            else {
                await call.join()
            }
        }
        catch (e) {
            console.error(e)
        }
    }

    const preAuthCall = async () => {
        try {
            const call = Daily.createCallObject({ url: `https://startingfinance.daily.co/${lesson.room_name}`, token: token ?? '' })
            await call.preAuth({ url: `https://startingfinance.daily.co/${lesson.room_name}`, token: token })
            setCall(call)
            setCallPreAuthorized(true)
        }
        catch (e) {
            console.error(e)
            setRoomError(e)
        }
    }

    const getMeetingToken = async () => {
        try {
            let token = await api.get(`/teacher/lessons/${lesson.id}/token`)
            setToken(token.token ?? '')
        }
        catch (e) {
            console.error(e)
        }
    }

    const getLessonParticipant = async (lessonId) => {
        try {
            const participant = await api.get(`/teacher/lessons/${lessonId}/participant`)
            setTestCompleted(!!participant)
        }
        catch (e) {
            console.error(e)
        }
    }

    const steps = [
        {
            selector: '#test-cam',
            content: 'Attiva la videocamera, dovresti riuscire a vedere te stesso',
        },
        {
            selector: '#test-mic',
            content: 'Attiva il microfono, dovresti vedere il tuo audio attivo',
        },
        {
            selector: '#test-speaker',
            content: 'Attiva gli altoparlanti, dovresti riuscire a sentire te stesso',
        },
        {
            selector: '#test-connection',
            content: 'Esegui il test di connessione (circa 30 secondi)',
        }
    ]


    return (
        <HeaderFooterLayout hideHeader={!lessonState || lessonState === LessonState.Live} hideFooter={!lessonState || lessonState === LessonState.Live}>
            {lessonState === LessonState.Live && call && !roomError &&
                <>
                    {testCompleted === null &&
                        <div className={styles.container} style={{ gap: '1rem' }}>
                            <div className={typo.subtitle}>Entrando nella lezione...</div>
                        </div>
                    }
                    {testCompleted &&
                        <DailyProvider callObject={call}>
                            <PiPProvider>
                                <Room lesson={lesson} />
                            </PiPProvider>
                        </DailyProvider>
                    }
                    {/* {testCompleted === false &&
                        <DailyProvider callObject={call}>
                            <TourProvider
                                disableInteraction={true}
                                disableKeyboardNavigation={true}
                                onClickMask={() => {
                                    //this disable the click to close the tour
                                }}
                                onClickHighlighted={(e, clickProps) => {
                                    let id = clickProps.steps[clickProps.currentStep].selector
                                    let element = document.querySelector(id)
                                    element.click()
                                    setTimeout(() => {
                                        clickProps.setCurrentStep(clickProps.currentStep + 1)
                                    }, 1000)
                                }}
                                steps={steps}
                                styles={{
                                    popover: (base) => ({
                                        ...base,
                                        '--reactour-accent': 'var(--secondary)',
                                        borderRadius: '1rem',
                                    }),
                                    maskArea: (base) => ({
                                        ...base,
                                        rx: '1.5rem'
                                    }),
                                    controls: (base) => ({
                                        ...base,
                                        display: 'none',
                                    }),
                                    close: (base) => ({
                                        ...base,
                                        display: 'none',
                                    }),
                                }}>
                                <RoomTest lesson={lesson} onTestCompleted={() => {
                                    setTestCompleted(true)
                                }} />
                            </TourProvider>
                        </DailyProvider>
                    } */}
                </>
            }
            {lessonState === LessonState.TooEarly && !roomError &&
                <div className={styles.container} style={{ gap: '1rem' }}>
                    <div className={typo.subtitle}>Troppo presto...</div>
                    <div className={typo.headline}>La lezione inizia il {formatDateV2(lesson.starts_at, { day: 'numeric', month: 'long', year: 'numeric' }, 'it', true, true)} alle {formatTimeV2(lesson.starts_at)} e sarà accessibile da due ore prima.</div>
                    <Button onClick={() => {
                        navigate(-1)
                    }}>TORNA INDIETRO</Button>
                </div>
            }
            {lessonState === LessonState.Ended && !lesson.video_url && !roomError &&
                <div className={styles.container} style={{ gap: '1rem' }}>
                    <div className={`${typo.subtitle} ${styles.endedTextBlack}`}>Lezione terminata</div>
                    <div className={`${typo.headline} ${styles.endedTextBlack}`}>La lezione è terminata il {formatDateV2(lesson.ended_at ?? lesson.ends_at, { day: 'numeric', month: 'long', year: 'numeric' }, 'it', true, true)} alle {formatTimeV2(lesson.ended_at ?? lesson.ends_at)}, la registrazione sarà disponibile a breve.</div>
                    <Button
                        style={{ fontSize: '1.125rem', padding: '1rem 3rem' }}
                        onClick={() => {
                            navigate('/')
                        }}>TORNA INDIETRO</Button>
                </div>
            }
            {lessonState === LessonState.Ended && lesson.video_url && !roomError &&
                <Lesson />
            }
            {lessonState && roomError &&
                <div className={styles.container}>
                    <div className={`${typo.subtitle} ${isDarkLessonTheme ? styles.endedTextLight : ''}`} style={{ paddingBottom: '1rem' }}>
                        {(meetingState === 'left-meeting' || !!lesson?.terminated_at) ? 'Lezione Terminata' : 'Lezione non disponibile'}
                    </div>
                    {(meetingState === 'left-meeting' || !!lesson?.terminated_at) &&
                        <div className={`${typo.body} ${isDarkLessonTheme ? styles.endedTextLight : ''}`} style={{ marginBottom: '1rem' }}>
                            Tu o un altro docente o tutor avete terminato la lezione
                        </div>
                    }
                    {(meetingState !== 'left-meeting' && !lesson?.terminated_at) &&
                        <div className={`${typo.headline} ${isDarkLessonTheme ? styles.endedTextLight : ''}`}>{roomError?.error?.msg}</div>
                    }
                    <Button 
                    style={{padding: '1rem 3rem'}}
                    onClick={() => {
                        window.location.href = '/'
                    }}>TORNA INDIETRO</Button>
                </div>
            }
            {lessonState === null &&
                <div className={styles.container}>
                    <Loader />
                    <div className={typo.headline}>
                        Caricamento...
                    </div>
                </div>
            }
        </HeaderFooterLayout>
    )

}

export default LiveLesson;